import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  UserPlus,
  UserMinus,
  Crown,
  ArrowUp,
  ArrowDown,
  MessageCircle,
  Send,
  Loader2,
  CheckSquare,
  Square,
} from "lucide-react";
import Header from "../Header";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AddGroupModal from "./AddGroupModal";
import RemoveParticipantModal from "./RemoveParticipantModal";
import CreateGroupModal from "./CreateGroupModal";
import PromoteParticipantModal from "./PromoteParticipantModal";
import DemoteParticipantModal from "./DemoteParticipantModal";
import SendMessageModal from "./SendMessageModal";
import SendMessageToGroupModal from "./SendMessageToGroupModal";
import { API_BASE_URL } from "../../../config";
import Layout from "../../Layout/Layout";
import { useTheme } from "../../../contexts/ThemeContext";

const GroupDetails = () => {
  const { theme } = useTheme();
  const { groupId } = useParams();
  const [groupDetail, setGroupDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [isRemoveMemberModalOpen, setIsRemoveMemberModalOpen] = useState(false);
  const [isPromoteMemberModalOpen, setIsPromoteMemberModalOpen] =
    useState(false);
  const [isDemoteMemberModalOpen, setIsDemoteMemberModalOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [createGroupLoading, setCreateGroupLoading] = useState(false);
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const [selectedMemberToRemove, setSelectedMemberToRemove] = useState(null);
  const [selectedMemberToPromote, setSelectedMemberToPromote] = useState(null);
  const [selectedMemberToDemote, setSelectedMemberToDemote] = useState(null);
  const [removeMemberLoading, setRemoveMemberLoading] = useState(false);
  const [promoteMemberLoading, setPromoteMemberLoading] = useState(false);
  const [demoteMemberLoading, setDemoteMemberLoading] = useState(false);
  const [selectedMemberToAdd, setSelectedMemberToAdd] = useState(null);
  const [groupIdToAdd, setGroupIdToAdd] = useState("");
  const [isSendMessageModalOpen, setIsSendMessageModalOpen] = useState(false);
  const [personalizedMessage, setPersonalizedMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [isSendMessageToGroupModalOpen, setIsSendMessageToGroupModalOpen] =
    useState(false);

  const handleSelectMember = (memberId) => {
    setSelectedMembers((prevState) => {
      if (prevState.includes(memberId)) {
        return prevState.filter((id) => id !== memberId);
      }
      return [...prevState, memberId];
    });
  };

  const handleSelectAll = () => {
    if (selectedMembers.length === groupDetail.members.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(groupDetail.members.map((member) => member.id));
    }
  };

  const handleSendMessage = () => {
    if (selectedMembers.length === 0) {
      toast.error("Please select at least one member to send a message.");
      return;
    }
    setIsSendMessageModalOpen(true);
  };

  // Fetch Group Details
  useEffect(() => {
    const fetchGroupDetail = async () => {
      try {
        const token = window.__appSession.getToken();
        const response = await fetch(
          `${API_BASE_URL}/api/whatsapp/${groupId}/group-details`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ groupId }),
          },
        );

        const data = await response.json();

        if (response.ok) {
          setGroupDetail(data.groupDetail.result);
        } else {
          setError("You are not Member of this Group Now.");
        }
      } catch (err) {
        setError("An error occurred while fetching data.");
      } finally {
        setLoading(false);
      }
    };

    fetchGroupDetail();
  }, [groupId]);

  const handleCreateGroup = async () => {
    setCreateGroupLoading(true);
    try {
      const token = window.__appSession.getToken();
      const response = await fetch(
        `${API_BASE_URL}/api/whatsapp/createsgroup`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            groupName,
            chatIds: selectedMembers,
          }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        toast.success("Group created successfully!", { autoClose: 3000 });
        setIsModalOpen(false);
        setGroupName("");
        setSelectedMembers([]);
        window.location.reload();
      } else {
        toast.error("Failed to create group.", { autoClose: 3000 });
      }
    } catch (err) {
      toast.error("An error occurred while creating the group.", {
        autoClose: 3000,
      });
    } finally {
      setCreateGroupLoading(false);
    }
  };

  const handleAddMemberToGroup = async (chatId) => {
    setAddMemberLoading(true);
    try {
      const token = window.__appSession.getToken();
      const response = await fetch(
        `${API_BASE_URL}/api/whatsapp/addgroupparticipant`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            groupId: groupIdToAdd,
            chatId: chatId,
          }),
        },
      );

      if (response.ok) {
        toast.success("Member added successfully!", { autoClose: 3000 });
        setIsAddMemberModalOpen(false);
        window.location.reload();
      } else {
        toast.error("Failed to add member to the group.", { autoClose: 3000 });
      }
    } catch (err) {
      toast.error("An error occurred while adding the member.", {
        autoClose: 3000,
      });
    } finally {
      setAddMemberLoading(false);
    }
  };

  const handleRemoveMemberFromGroup = async () => {
    setRemoveMemberLoading(true);
    try {
      const token = window.__appSession.getToken();
      const response = await fetch(
        `${API_BASE_URL}/api/whatsapp/removegroupparticipant`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            groupId: groupId,
            chatId: selectedMemberToRemove,
          }),
        },
      );

      if (response.ok) {
        toast.success("Member removed successfully!", { autoClose: 3000 });
        setIsRemoveMemberModalOpen(false);
        window.location.reload();
      } else {
        toast.error("Failed to remove member from the group.", {
          autoClose: 3000,
        });
      }
    } catch (err) {
      toast.error("An error occurred while removing the member.", {
        autoClose: 3000,
      });
    } finally {
      setRemoveMemberLoading(false);
    }
  };

  const handlePromoteMemberFromGroup = async () => {
    setPromoteMemberLoading(true);
    try {
      const token = window.__appSession.getToken();
      const response = await fetch(
        `${API_BASE_URL}/api/whatsapp/promotegroupparticipant`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            groupId: groupId,
            chatId: selectedMemberToPromote,
          }),
        },
      );

      if (response.ok) {
        toast.success("Member promoted successfully!", { autoClose: 3000 });
        setIsPromoteMemberModalOpen(false);
        window.location.reload();
      } else {
        toast.error("Failed to promote member from the group.", {
          autoClose: 3000,
        });
      }
    } catch (err) {
      toast.error("An error occurred while promoting the member.", {
        autoClose: 3000,
      });
    } finally {
      setPromoteMemberLoading(false);
    }
  };

  const handleDemoteMemberFromGroup = async () => {
    setDemoteMemberLoading(true);
    try {
      const token = window.__appSession.getToken();
      const response = await fetch(
        `${API_BASE_URL}/api/whatsapp/demotegroupparticipant`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            groupId: groupId,
            chatId: selectedMemberToDemote,
          }),
        },
      );

      if (response.ok) {
        toast.success("Member Demoted successfully!", { autoClose: 3000 });
        setIsDemoteMemberModalOpen(false);
        window.location.reload();
      } else {
        toast.error("Failed to Demote member from the group.", {
          autoClose: 3000,
        });
      }
    } catch (err) {
      toast.error("An error occurred while demoting the member.", {
        autoClose: 3000,
      });
    } finally {
      setDemoteMemberLoading(false);
    }
  };

  const actionButtons = [
    {
      label: "Create Group",
      icon: Users,
      onClick: () => setIsModalOpen(true),
      className: `${theme.accentColor} ${theme.buttonHover}`,
    },
    {
      label: "Add Member",
      icon: UserPlus,
      onClick: () => setIsAddMemberModalOpen(true),
      className: "bg-emerald-500 hover:bg-emerald-600",
    },
    {
      label: "Remove Member",
      icon: UserMinus,
      onClick: () => setIsRemoveMemberModalOpen(true),
      className: "bg-red-500 hover:bg-red-600",
    },
    {
      label: "Promote",
      icon: ArrowUp,
      onClick: () => setIsPromoteMemberModalOpen(true),
      className: "bg-primary-500 hover:bg-primary-600",
    },
    {
      label: "Demote",
      icon: ArrowDown,
      onClick: () => setIsDemoteMemberModalOpen(true),
      className: "bg-orange-500 hover:bg-orange-600",
    },
    {
      label: "Message Member",
      icon: MessageCircle,
      onClick: handleSendMessage,
      className: "bg-purple-500 hover:bg-purple-600",
    },
    {
      label: "Message Group",
      icon: Send,
      onClick: () => setIsSendMessageToGroupModalOpen(true),
      className: "bg-indigo-500 hover:bg-indigo-600",
    },
  ];

  return (
    <>
      <Layout>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className=""
        >
          <div
            className={`max-w-7xl mx-auto ${theme.contentBg} ${theme.contentBorder} border shadow-xl rounded-xl overflow-hidden`}
          >
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className={`${theme.grpBG} ${theme.headerBorder} border-b `}
            >
              <div className="flex items-center px-4 gap-2 py-1">
                <div className={`${theme.logoBG} p-2 rounded-full`}>
                  <Users className={`size-4 ${theme.iconColor}`} />
                </div>
                <div className="text-left">
                  <h1
                    className={`text-lg sm:text-xl font-bold ${theme.headerText}`}
                  >
                    Group Details
                  </h1>
                  <p className={`text-xs  ${theme.headerText}`}>
                    Manage your group members and settings
                  </p>
                </div>
              </div>
            </motion.div>

            <div className="p-2">
              <AnimatePresence mode="wait">
                {loading && (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="flex flex-col items-center justify-center py-12"
                  >
                    <Loader2
                      className={`w-8 h-8 animate-spin ${theme.iconColor} mb-3`}
                    />
                    <p className={`text-sm ${theme.sidebarText}`}>
                      Loading group details...
                    </p>
                  </motion.div>
                )}

                {error && !loading && (
                  <motion.div
                    key="error"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="flex flex-col items-center justify-center py-12"
                  >
                    <div className="bg-red-100 border border-red-300 rounded-lg p-4 text-center">
                      <p className="text-red-700 font-medium text-sm">
                        {error}
                      </p>
                    </div>
                  </motion.div>
                )}

                {groupDetail && groupDetail.members && !loading && !error && (
                  <motion.div
                    key="content"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="space-y-4"
                  >
                    {/* Group Info */}
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.3 }}
                      className={`${theme.sidebarBg} ${theme.sidebarBorder} border rounded-lg p-2`}
                    >
                      <div className="flex  flex-col md:flex-row md:items-center gap-6 md:gap-2 md:divide-x divide-gray-200">
                        <div className="flex items-center gap-2">
                          <div
                            className={`${theme.logoBG} p-2 rounded-full w-fit`}
                          >
                            <Users className={`size-4 ${theme.iconColor}`} />
                          </div>
                          <div>
                            <h2
                              className={`text-lg sm:text-xl font-bold ${theme.titleColor}`}
                            >
                              {groupDetail.name}
                            </h2>
                            <div className="flex flex-col sm:flex-row justify-center items-center space-y-1 sm:space-y-0 sm:space-x-4 text-xs sm:text-sm">
                              <span
                                className={`${theme.sidebarText} opacity-75`}
                              >
                                ID: {groupDetail.id}
                              </span>
                              <span
                                className={`${theme.sidebarText} opacity-75 flex items-center`}
                              >
                                <Crown className="w-3 h-3 mr-1" />
                                Owner: {groupDetail.ownerName}
                              </span>
                            </div>
                          </div>
                        </div>
                        {/* Action Buttons */}
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6, delay: 0.4 }}
                          className="flex flex-wrap gap-2 md:pl-2"
                        >
                          {actionButtons.map((button, index) => (
                            <motion.button
                              key={button.label}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.5, delay: 0.1 * index }}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={button.onClick}
                              className={`${button.className} text-white px-2 py-2 rounded-lg shadow-sm transition-all duration-200 flex items-center justify-center gap-2 text-xs font-medium`}
                            >
                              <button.icon className="w-3 h-3" />
                              <span className="hidden sm:block">
                                {button.label}
                              </span>
                            </motion.button>
                          ))}
                        </motion.div>
                      </div>
                    </motion.div>

                    {/* Members Section */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.5 }}
                      className="space-y-4"
                    >
                      <div className="flex items-center justify-between">
                        <h3
                          className={`text-base sm:text-lg font-semibold ${theme.titleColor} flex items-center`}
                        >
                          <Users className="w-4 h-4 mr-2" />
                          Members
                          <span className="ml-2 text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full">
                            {groupDetail.members.length}
                          </span>
                        </h3>
                      </div>

                      {groupDetail.members.length === 0 ? (
                        <div
                          className={`${theme.sidebarBg} ${theme.sidebarBorder} border rounded-lg p-6 text-center`}
                        >
                          <Users
                            className={`w-12 h-12 ${theme.iconColor} opacity-50 mx-auto mb-3`}
                          />
                          <p
                            className={`text-sm ${theme.sidebarText} opacity-75`}
                          >
                            No members available in this group.
                          </p>
                        </div>
                      ) : (
                        <div
                          className={`${theme.sidebarBg} ${theme.sidebarBorder} border rounded-lg overflow-hidden`}
                        >
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-200 top-0 sticky z-10">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-800">
                                    <motion.button
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.9 }}
                                      onClick={handleSelectAll}
                                      className="flex items-center"
                                    >
                                      {groupDetail.members.length > 0 &&
                                      selectedMembers.length ===
                                        groupDetail.members.length ? (
                                        <CheckSquare
                                          className={`w-4 h-4 ${theme.iconColor}`}
                                        />
                                      ) : (
                                        <Square
                                          className={`w-4 h-4 ${theme.iconColor}`}
                                        />
                                      )}
                                    </motion.button>
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-800">
                                    #
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-800">
                                    Name
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-800">
                                    ID
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-800">
                                    Role
                                  </th>
                                </tr>
                              </thead>
                              <tbody
                                className={`${theme.sidebarBg} divide-y divide-gray-200`}
                              >
                                {groupDetail.members.map((member, index) => (
                                  <motion.tr
                                    key={member.id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{
                                      duration: 0.3,
                                      delay: 0.1 * index,
                                    }}
                                    className={`${theme.sidebarHover} transition-colors duration-200`}
                                  >
                                    <td className="px-3 py-1">
                                      <motion.button
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        onClick={() =>
                                          handleSelectMember(member.id)
                                        }
                                      >
                                        {selectedMembers.includes(member.id) ? (
                                          <CheckSquare
                                            className={`w-4 h-4 ${theme.iconColor}`}
                                          />
                                        ) : (
                                          <Square
                                            className={`w-4 h-4 ${theme.iconColor}`}
                                          />
                                        )}
                                      </motion.button>
                                    </td>
                                    <td
                                      className={`px-3 py-1 text-xs ${theme.sidebarText} opacity-75`}
                                    >
                                      {index + 1}
                                    </td>
                                    <td
                                      className={`px-3 py-1 text-xs ${theme.sidebarText} font-medium`}
                                    >
                                      {member.name || "Unnamed"}
                                    </td>
                                    <td
                                      className={`px-3 py-1 text-xs ${theme.sidebarText} opacity-75 font-mono`}
                                    >
                                      {member.id}
                                    </td>
                                    <td className="px-3 py-1">
                                      {member.admin ? (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                          <Crown className="w-3 h-3 mr-1" />
                                          Admin
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                          Member
                                        </span>
                                      )}
                                    </td>
                                  </motion.tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </Layout>

      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Modals */}
      <AnimatePresence>
        {isAddMemberModalOpen && (
          <AddGroupModal
            members={groupDetail.members}
            groupIdToAdd={groupIdToAdd}
            setGroupIdToAdd={setGroupIdToAdd}
            onClose={() => setIsAddMemberModalOpen(false)}
            onSubmit={handleAddMemberToGroup}
            loading={addMemberLoading}
            selectedMember={selectedMemberToAdd}
            setSelectedMember={setSelectedMemberToAdd}
          />
        )}

        {isRemoveMemberModalOpen && (
          <RemoveParticipantModal
            members={groupDetail.members}
            selectedMember={selectedMemberToRemove}
            setSelectedMember={setSelectedMemberToRemove}
            onClose={() => setIsRemoveMemberModalOpen(false)}
            onSubmit={handleRemoveMemberFromGroup}
            loading={removeMemberLoading}
          />
        )}

        {isPromoteMemberModalOpen && (
          <PromoteParticipantModal
            members={groupDetail.members}
            selectedMember={selectedMemberToPromote}
            setSelectedMember={setSelectedMemberToPromote}
            onClose={() => setIsPromoteMemberModalOpen(false)}
            onSubmit={handlePromoteMemberFromGroup}
            loading={promoteMemberLoading}
          />
        )}

        {isDemoteMemberModalOpen && (
          <DemoteParticipantModal
            members={groupDetail.members}
            selectedMember={selectedMemberToDemote}
            setSelectedMember={setSelectedMemberToDemote}
            onClose={() => setIsDemoteMemberModalOpen(false)}
            onSubmit={handleDemoteMemberFromGroup}
            loading={demoteMemberLoading}
          />
        )}

        {isModalOpen && (
          <CreateGroupModal
            groupName={groupName}
            setGroupName={setGroupName}
            selectedMembers={selectedMembers}
            setSelectedMembers={setSelectedMembers}
            groupDetail={groupDetail}
            handleCreateGroup={handleCreateGroup}
            createGroupLoading={createGroupLoading}
            onClose={() => setIsModalOpen(false)}
          />
        )}

        {isSendMessageModalOpen && (
          <SendMessageModal
            selectedMembers={selectedMembers}
            setIsSendMessageModalOpen={setIsSendMessageModalOpen}
            personalizedMessage={personalizedMessage}
            setPersonalizedMessage={setPersonalizedMessage}
            files={files}
            setFiles={setFiles}
          />
        )}

        {isSendMessageToGroupModalOpen && (
          <SendMessageToGroupModal
            groupId={groupDetail.id}
            setIsSendMessageToGroupModalOpen={setIsSendMessageToGroupModalOpen}
            personalizedMessage={personalizedMessage}
            setPersonalizedMessage={setPersonalizedMessage}
            files={files}
            setFiles={setFiles}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default GroupDetails;

// import React, { useState, useEffect } from "react";
// import { useParams } from "react-router-dom";
// import Header from "../Header";
// import { ToastContainer, toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import AddGroupModal from "./AddGroupModal";
// import RemoveParticipantModal from "./RemoveParticipantModal";
// import CreateGroupModal from "./CreateGroupModal";
// import PromoteParticipantModal from "./PromoteParticipantModal";
// import DemoteParticipantModal from "./DemoteParticipantModal";
// import SendMessageModal from "./SendMessageModal";
// import SendMessageToGroupModal from "./SendMessageToGroupModal";
// import { API_BASE_URL } from "../../../config";
// import Layout from "../../Layout/Layout";
// const GroupDetails = () => {
//   const { groupId } = useParams();
//   const [groupDetail, setGroupDetail] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState("");
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
//   const [isRemoveMemberModalOpen, setIsRemoveMemberModalOpen] = useState(false);
//   const [isPromoteMemberModalOpen, setIsPromoteMemberModalOpen] =
//     useState(false);
//   const [isDemoteMemberModalOpen, setIsDemoteMemberModalOpen] = useState(false);
//   const [groupName, setGroupName] = useState("");
//   const [selectedMembers, setSelectedMembers] = useState([]);
//   const [createGroupLoading, setCreateGroupLoading] = useState(false);
//   const [addMemberLoading, setAddMemberLoading] = useState(false);
//   const [selectedMemberToRemove, setSelectedMemberToRemove] = useState(null);
//   const [selectedMemberToPromote, setSelectedMemberToPromote] = useState(null);
//   const [selectedMemberToDemote, setSelectedMemberToDemote] = useState(null);
//   const [removeMemberLoading, setRemoveMemberLoading] = useState(false);
//   const [promoteMemberLoading, setPromoteMemberLoading] = useState(false);
//   const [demoteMemberLoading, setDemoteMemberLoading] = useState(false);
//   const [selectedMemberToAdd, setSelectedMemberToAdd] = useState(null);
//   const [groupIdToAdd, setGroupIdToAdd] = useState("");
//   const [isSendMessageModalOpen, setIsSendMessageModalOpen] = useState(false);
//   const [personalizedMessage, setPersonalizedMessage] = useState("");
//   const [files, setFiles] = useState([]);
//   const [isSendMessageToGroupModalOpen, setIsSendMessageToGroupModalOpen] =
//     useState(false);
//   // Function to handle checkbox change (select members)
//   const handleSelectMember = (memberId) => {
//     setSelectedMembers((prevState) => {
//       if (prevState.includes(memberId)) {
//         return prevState.filter((id) => id !== memberId);
//       }
//       return [...prevState, memberId];
//     });
//   };

//   const handleSelectAll = () => {
//     if (selectedMembers.length === groupDetail.members.length) {
//       // If all members are selected, unselect all
//       setSelectedMembers([]);
//     } else {
//       // Otherwise, select all members
//       setSelectedMembers(groupDetail.members.map((member) => member.id));
//     }
//   };

//   const handleSendMessage = () => {
//     if (selectedMembers.length === 0) {
//       toast.error("Please select at least one member to send a message.");
//       return;
//     }
//     setIsSendMessageModalOpen(true);
//   };
//   // Fetch Group Details
//   useEffect(() => {
//     const fetchGroupDetail = async () => {
//       try {
//         const token = window.__appSession.getToken();

//         const response = await fetch(
//           `${API_BASE_URL}/api/whatsapp/${groupId}/group-details`,
//           {
//             method: "POST",
//             headers: {
//               "Content-Type": "application/json",
//               Authorization: `Bearer ${token}`,
//             },
//             body: JSON.stringify({ groupId }),
//           }
//         );

//         const data = await response.json();

//         if (response.ok) {
//           setGroupDetail(data.groupDetail.result);
//         } else {
//           setError("You are not Member of this Group Now.");
//         }
//       } catch (err) {
//         setError("An error occurred while fetching data.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchGroupDetail();
//   }, [groupId]); // Re-fetch when groupId changes

//   // Function to handle Create Group API request
//   const handleCreateGroup = async () => {
//     setCreateGroupLoading(true);
//     try {
//       const token = window.__appSession.getToken();

//       const response = await fetch(
//         `${API_BASE_URL}/api/whatsapp/createsgroup`,
//         {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//             Authorization: `Bearer ${token}`,
//           },
//           body: JSON.stringify({
//             groupName,
//             chatIds: selectedMembers,
//           }),
//         }
//       );

//       const data = await response.json();

//       if (response.ok) {
//         toast.success("Group created successfully!", {
//           autoClose: 3000,
//         });
//         // Close modal and reset form
//         setIsModalOpen(false);
//         setGroupName("");
//         setSelectedMembers([]);
//         window.location.reload();
//       } else {
//         toast.error("Failed to create group.", {
//           autoClose: 3000,
//         });
//       }
//     } catch (err) {
//       toast.error("An error occurred while creating the group.", {
//         autoClose: 3000,
//       });
//     } finally {
//       setCreateGroupLoading(false);
//     }
//   };

//   // Function to handle Add Member to Group API request
//   const handleAddMemberToGroup = async (chatId) => {
//     setAddMemberLoading(true);
//     try {
//       const token = window.__appSession.getToken();

//       const response = await fetch(
//         `${API_BASE_URL}/api/whatsapp/addgroupparticipant`,
//         {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//             Authorization: `Bearer ${token}`,
//           },
//           body: JSON.stringify({
//             groupId: groupIdToAdd,
//             chatId: chatId,
//           }),
//         }
//       );

//       if (response.ok) {
//         toast.success("Member added successfully!", { autoClose: 3000 });
//         setIsAddMemberModalOpen(false);
//         window.location.reload();
//       } else {
//         toast.error("Failed to add member to the group.", { autoClose: 3000 });
//       }
//     } catch (err) {
//       toast.error("An error occurred while adding the member.", {
//         autoClose: 3000,
//       });
//     } finally {
//       setAddMemberLoading(false);
//     }
//   };

//   // Function to handle Remove Member from Group
//   const handleRemoveMemberFromGroup = async () => {
//     setRemoveMemberLoading(true);
//     try {
//       const token = window.__appSession.getToken();

//       const response = await fetch(
//         `${API_BASE_URL}/api/whatsapp/removegroupparticipant`,
//         {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//             Authorization: `Bearer ${token}`,
//           },
//           body: JSON.stringify({
//             groupId: groupId, // Use groupId of the current group
//             chatId: selectedMemberToRemove, // The selected member to remove
//           }),
//         }
//       );

//       if (response.ok) {
//         toast.success("Member removed successfully!", { autoClose: 3000 });
//         setIsRemoveMemberModalOpen(false);
//         window.location.reload();
//       } else {
//         toast.error("Failed to remove member from the group.", {
//           autoClose: 3000,
//         });
//       }
//     } catch (err) {
//       toast.error("An error occurred while removing the member.", {
//         autoClose: 3000,
//       });
//     } finally {
//       setRemoveMemberLoading(false);
//     }
//   };

//   // Function to handle Promote Member from Group

//   const handlePromoteMemberFromGroup = async () => {
//     setPromoteMemberLoading(true);
//     try {
//       const token = window.__appSession.getToken();

//       const response = await fetch(
//         `${API_BASE_URL}/api/whatsapp/promotegroupparticipant`,
//         {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//             Authorization: `Bearer ${token}`,
//           },
//           body: JSON.stringify({
//             groupId: groupId, // Use groupId of the current group
//             chatId: selectedMemberToPromote, // The selected member to remove
//           }),
//         }
//       );

//       if (response.ok) {
//         toast.success("Member promoted successfully!", { autoClose: 3000 });
//         setIsPromoteMemberModalOpen(false);
//         window.location.reload();
//       } else {
//         toast.error("Failed to promote member from the group.", {
//           autoClose: 3000,
//         });
//       }
//     } catch (err) {
//       toast.error("An error occurred while promoting the member.", {
//         autoClose: 3000,
//       });
//     } finally {
//       setPromoteMemberLoading(false);
//     }
//   };

//   const handleDemoteMemberFromGroup = async () => {
//     setDemoteMemberLoading(true);
//     try {
//       const token = window.__appSession.getToken();

//       const response = await fetch(
//         `${API_BASE_URL}/api/whatsapp/demotegroupparticipant`,
//         {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//             Authorization: `Bearer ${token}`,
//           },
//           body: JSON.stringify({
//             groupId: groupId, // Use groupId of the current group
//             chatId: selectedMemberToDemote, // The selected member to remove
//           }),
//         }
//       );

//       if (response.ok) {
//         toast.success("Member Demoted successfully!", { autoClose: 3000 });
//         setIsDemoteMemberModalOpen(false);
//         window.location.reload();
//       } else {
//         toast.error("Failed to Demote member from the group.", {
//           autoClose: 3000,
//         });
//       }
//     } catch (err) {
//       toast.error("An error occurred while demoting the member.", {
//         autoClose: 3000,
//       });
//     } finally {
//       setDemoteMemberLoading(false);
//     }
//   };

//   return (
//     <Layout>
//       <div className="min-h-screen bg-gray-50 pt-2 pb-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
//         <div className="max-w-7xl w-full bg-white shadow-lg rounded-lg p-6 relative">
//           <header className="text-center mb-6">
//             <h1 className="text-3xl font-semibold text-indigo-600 sm:text-2xl">
//               Group Details
//             </h1>
//             <p className="mt-2 text-lg text-gray-500 sm:text-base">
//               Details of the group and its members
//             </p>
//           </header>

//           {loading && (
//             <div className="text-center py-6">
//               <div className="animate-spin border-t-4 border-indigo-600 rounded-full w-16 h-16 mx-auto mb-4"></div>
//               <p className="text-xl text-gray-500">Loading...</p>
//             </div>
//           )}

//           {error && !loading && (
//             <div className="text-center py-6 text-red-600">
//               <p className="text-xl">{error}</p>
//             </div>
//           )}

//           {groupDetail && groupDetail.members && !loading && !error && (
//             <div>
//               <div className="text-center mb-6">
//                 <h2 className="text-2xl font-semibold text-indigo-700 sm:text-xl">
//                   {groupDetail.name}
//                 </h2>
//                 <p className="text-lg text-gray-600 sm:text-base">
//                   Group ID: {groupDetail.id}
//                 </p>
//                 <p className="text-lg text-gray-600 sm:text-base">
//                   Owner: {groupDetail.ownerName}
//                 </p>
//               </div>

//               {/* Button Section */}
//               <div className="grid grid-cols-1 sm:grid-cols-7 gap-1 mb-4">
//                 {/* Create Group Button */}
//                 <button
//                   onClick={() => setIsModalOpen(true)}
//                   className="bg-indigo-600 text-white px-2 py-1 rounded-lg shadow-md hover:bg-indigo-700 sm:col-span-1 text-sm"
//                 >
//                   Create Group
//                 </button>

//                 {/* Add Member Button */}
//                 <button
//                   onClick={() => setIsAddMemberModalOpen(true)}
//                   className="bg-green-600 text-white px-2 py-1 rounded-lg shadow-md hover:bg-green-700 sm:col-span-1 text-sm"
//                 >
//                   Add Member to Group
//                 </button>

//                 {/* Remove Participant Button */}
//                 <button
//                   onClick={() => setIsRemoveMemberModalOpen(true)}
//                   className="bg-red-600 text-white px-2 py-1 rounded-lg shadow-md hover:bg-red-700 sm:col-span-1 text-sm"
//                 >
//                   Remove Participant
//                 </button>

//                 <button
//                   onClick={() => setIsPromoteMemberModalOpen(true)}
//                   className="bg-yellow-600 text-white px-2 py-1 rounded-lg shadow-md hover:bg-yellow-700 sm:col-span-1 text-sm"
//                 >
//                   Promote Participant
//                 </button>

//                 <button
//                   onClick={() => setIsDemoteMemberModalOpen(true)}
//                   className="bg-orange-600 text-white px-2 py-1 rounded-lg shadow-md hover:bg-orange-700 sm:col-span-1 text-sm"
//                 >
//                   Demote Participant
//                 </button>

//                 <button
//                   onClick={handleSendMessage}
//                   className="bg-indigo-600 text-white px-2 py-1 rounded-lg shadow-md hover:bg-indigo-700 sm:col-span-1 text-sm"
//                 >
//                   Message To Member
//                 </button>

//                 <button
//                   onClick={() => setIsSendMessageToGroupModalOpen(true)} // Open group message modal
//                   className="bg-indigo-600 text-white px-2 py-1 rounded-lg shadow-md hover:bg-indigo-700 sm:col-span-1 text-sm"
//                 >
//                   Message to Group
//                 </button>
//               </div>

//               <div className="space-y-4">
//                 <h3 className="text-xl font-semibold text-indigo-600 mb-4 sm:text-lg">
//                   Members:{" "}
//                   <span className="text-red-600">
//                     {groupDetail.members.length} Members
//                   </span>
//                 </h3>

//                 {/* Check if there are members */}
//                 {groupDetail.members.length === 0 ? (
//                   <p className="text-red-600 text-sm mb-4 sm:text-xs">
//                     No members available in this group.
//                   </p>
//                 ) : (
//                   <div className="overflow-x-auto">
//                     <table className="min-w-full bg-white border border-gray-300 shadow-md rounded-lg">
//                       <thead>
//                         <tr className="bg-indigo-600 text-white">
//                           {/* "Select All" Checkbox */}
//                           <th className="py-1 px-2 text-left text-sm sm:text-xs">
//                             <input
//                               type="checkbox"
//                               checked={
//                                 groupDetail.members.length > 0 &&
//                                 selectedMembers.length ===
//                                   groupDetail.members.length
//                               }
//                               onChange={handleSelectAll}
//                               className="w-4 h-4"
//                             />
//                           </th>
//                           <th className="py-2 px-4 text-left text-sm sm:text-xs">
//                             Serial Number
//                           </th>
//                           <th className="py-2 px-4 text-left text-sm sm:text-xs">
//                             Member Name
//                           </th>
//                           <th className="py-2 px-4 text-left text-sm sm:text-xs">
//                             Member ID
//                           </th>
//                           <th className="py-2 px-4 text-left text-sm sm:text-xs">
//                             Role
//                           </th>
//                         </tr>
//                       </thead>
//                       <tbody>
//                         {groupDetail.members.map((member, index) => (
//                           <tr
//                             key={member.id}
//                             className="hover:bg-indigo-50 transition"
//                           >
//                             <td className="py-1 px-2">
//                               <input
//                                 type="checkbox"
//                                 checked={selectedMembers.includes(member.id)}
//                                 onChange={() => handleSelectMember(member.id)}
//                                 className="w-4 h-4"
//                               />
//                             </td>
//                             <td className="py-2 px-4 text-gray-500">
//                               {index + 1}
//                             </td>{" "}
//                             {/* Serial Number */}
//                             <td className="py-2 px-4">
//                               {member.name || "Unnamed"}
//                             </td>
//                             <td className="py-2 px-4 text-gray-500">
//                               {member.id}
//                             </td>
//                             <td className="py-2 px-4">
//                               {member.admin ? (
//                                 <span className="text-green-500 font-semibold">
//                                   Admin
//                                 </span>
//                               ) : (
//                                 <span className="text-gray-500">Member</span>
//                               )}
//                             </td>
//                           </tr>
//                         ))}
//                       </tbody>
//                     </table>
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}
//         </div>
//         <ToastContainer />
//       </div>

//       {isAddMemberModalOpen && (
//         <AddGroupModal
//           members={groupDetail.members}
//           groupIdToAdd={groupIdToAdd}
//           setGroupIdToAdd={setGroupIdToAdd}
//           onClose={() => setIsAddMemberModalOpen(false)}
//           onSubmit={handleAddMemberToGroup}
//           loading={addMemberLoading}
//           selectedMember={selectedMemberToAdd}
//           setSelectedMember={setSelectedMemberToAdd}
//         />
//       )}

//       {isRemoveMemberModalOpen && (
//         <RemoveParticipantModal
//           members={groupDetail.members}
//           selectedMember={selectedMemberToRemove}
//           setSelectedMember={setSelectedMemberToRemove}
//           onClose={() => setIsRemoveMemberModalOpen(false)}
//           onSubmit={handleRemoveMemberFromGroup}
//           loading={removeMemberLoading}
//         />
//       )}
//       {isPromoteMemberModalOpen && (
//         <PromoteParticipantModal
//           members={groupDetail.members}
//           selectedMember={selectedMemberToPromote}
//           setSelectedMember={setSelectedMemberToPromote}
//           onClose={() => setIsPromoteMemberModalOpen(false)}
//           onSubmit={handlePromoteMemberFromGroup}
//           loading={promoteMemberLoading}
//         />
//       )}

//       {isDemoteMemberModalOpen && (
//         <DemoteParticipantModal
//           members={groupDetail.members}
//           selectedMember={selectedMemberToDemote}
//           setSelectedMember={setSelectedMemberToDemote}
//           onClose={() => setIsDemoteMemberModalOpen(false)}
//           onSubmit={handleDemoteMemberFromGroup}
//           loading={demoteMemberLoading}
//         />
//       )}

//       {/* Create Group Modal */}
//       {isModalOpen && (
//         <CreateGroupModal
//           groupName={groupName}
//           setGroupName={setGroupName}
//           selectedMembers={selectedMembers}
//           setSelectedMembers={setSelectedMembers}
//           groupDetail={groupDetail}
//           handleCreateGroup={handleCreateGroup}
//           createGroupLoading={createGroupLoading}
//           onClose={() => setIsModalOpen(false)}
//         />
//       )}

//       {isSendMessageModalOpen && (
//         <SendMessageModal
//           selectedMembers={selectedMembers}
//           setIsSendMessageModalOpen={setIsSendMessageModalOpen}
//           personalizedMessage={personalizedMessage}
//           setPersonalizedMessage={setPersonalizedMessage}
//           files={files}
//           setFiles={setFiles}
//         />
//       )}

//       {isSendMessageToGroupModalOpen && (
//         <SendMessageToGroupModal
//           groupId={groupDetail.id} // Pass the group ID to the modal
//           setIsSendMessageToGroupModalOpen={setIsSendMessageToGroupModalOpen}
//           personalizedMessage={personalizedMessage}
//           setPersonalizedMessage={setPersonalizedMessage}
//           files={files}
//           setFiles={setFiles}
//         />
//       )}
//     </Layout>
//   );
// };

// export default GroupDetails;
